/**
 * <mnr-design-picker> — MNR design picker (v5, Route B live fill).
 *
 * Rendered by snippets/mnr-picker.liquid. Owns the slot row and the tile
 * drawer, nothing else: no prices, no cart calls. Host blocks call
 *
 *   picker.setSlotCount(n)   the target count (a tier or a set); trims picks from the end
 *   picker.getSelection()    [{ variantId, name }] in pick order
 *   picker.preselect(id)     adds that variant (used for ?preselect=)
 *   picker.open({ mode, slots, preselect, opener })   opens the drawer (PDP swatches, 11 Oct 2026)
 *   picker.mode              'build' (default) | 'browse' — reflected as the `mode` attribute
 *
 * and listen for
 *
 *   mnr:selection-change   detail { selection, filled, total } after every change
 *   mnr:selection-limit    detail { max } when a plus was refused at the ceiling
 *   mnr:picker-closed      detail { filled, total } after "Selectie toepassen"
 *                          when no empty slot is left (host focuses its add button)
 *   mnr:mode-change        detail { mode } after the mode switched while open
 *
 * Browse mode: the same drawer as a catalogue. No − / + bars, no progress
 * footer (CSS on [mode="browse"]); a tile click navigates to data-url; the
 * footer button switches the open dialog to build mode. Slot row hidden
 * with data-hide-slots (the host decides). Build mode is byte-for-byte the
 * behaviour the Mix & Match and starter set blocks had before.
 *
 * Live fill (decided 19 Sep 2026): a plus or minus in the drawer changes the
 * slot row immediately; there is no draft and nothing is discarded on close.
 * Picks beyond the target count grow the row up to data-max; the host block
 * decides what that means (next tier, next set, or a refusal message).
 *
 * The drawer is a native <dialog> inside Horizon's <dialog-component>, so the
 * scroll lock, click-outside and Escape come from the theme (assets/dialog.js).
 * No setTimeout chains, no polling, no body.style.overflow writes.
 *
 * Every product name enters the DOM through textContent / setAttribute.
 *
 * (assets/mnr-design-picker.js of the old v3/v4 blocks was removed on 11 Oct 2026.)
 */
import '@theme/dialog';
import { CartAddEvent, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {{variantId: number, name: string, img: string, price: number}} Design */

/**
 * Shared add-to-cart for both bundle blocks (Route B, 19 Sep 2026): ONE line —
 * the bundle variant for the chosen count / set — with the designs in the
 * properties the cart transform app expands from. Same shape for the starter
 * set and for mix & match:
 *
 *   _components        JSON for the cart transform app:
 *                      [{"id": "gid://shopify/ProductVariant/<id>", "quantity": n}, …]
 *                      duplicates merged into quantity, order of first pick
 *                      (shape confirmed by the app's developer, 19 Sep 2026)
 *   _mnr_designs       variant ids, comma separated, slot order, repeats allowed
 *                      (the cart card reads this one)
 *   _mnr_design_count  number of designs
 *   _mnr_group_type    "starterset" | "mixmatch"
 *   _mnr_design_names  names in slot order, separated by " | " (hidden; cart card fallback)
 *
 * No visible "Design N" properties any more (19 Sep 2026): the cart transform
 * app lists the designs with images beneath the bundle in checkout and on the
 * order, so the text list only doubled them.
 *
 * Never a price. With `replaceKey` the line being edited ("Wijzigen") is
 * removed first via /cart/change.js. After the add: /cart.js, Horizon's
 * CartAddEvent dispatched from `host` (bubbles to document), the drawer opened.
 *
 * @param {object} o
 * @param {number|string} o.variantId - the bundle variant
 * @param {Array<{variantId: number, name: string}>} o.selection - picker.getSelection()
 * @param {'starterset'|'mixmatch'} o.groupType
 * @param {number} [o.designCount] - defaults to selection.length
 * @param {string} [o.replaceKey] - cart line key to remove first
 * @param {HTMLElement} o.host - element to dispatch the cart event from
 * @param {string} [o.productId]
 * @param {() => Promise<void> | void} [o.beforeNotify] - awaited after a successful add and
 *   before the cart event / drawer open (PDP: close the picker dialog first so the two
 *   modal dialogs never overlap and the body scroll pin is released in order)
 * @returns {Promise<{cart: any, item: any}>} throws Error(message) on failure
 */
/**
 * Merges the picks into [{id, quantity}] — duplicates become a quantity.
 * Confirmed with the Functions Creator developer (19 Sep 2026): keys are
 * exactly `id` and `quantity`, ids as Shopify GIDs, nothing else, no prices.
 * @param {Array<{variantId: number}>} selection
 */
export function componentsFor(selection) {
  const merged = new Map();
  for (const s of selection) merged.set(s.variantId, (merged.get(s.variantId) || 0) + 1);
  return [...merged].map(([id, quantity]) => ({ id: `gid://shopify/ProductVariant/${id}`, quantity }));
}

export async function addBundleToCart(o) {
  const count = o.designCount ?? o.selection.length;
  const properties = {
    _components: JSON.stringify(componentsFor(o.selection)),
    _mnr_designs: o.selection.map((s) => s.variantId).join(','),
    _mnr_design_count: String(count),
    _mnr_group_type: o.groupType,
    _mnr_design_names: o.selection.map((s) => s.name.replace(/\s*\|\s*/g, ' ')).join(' | '),
  };
  const item = { id: Number(o.variantId), quantity: 1, properties };

  const routes = /** @type {any} */ (window).Theme?.routes || {};
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  if (o.replaceKey) {
    // Editing: the old line goes first so the cart never holds both.
    const res = await fetch((routes.cart_change_url || '/cart/change') + '.js', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id: o.replaceKey, quantity: 0 }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'replace failed');
    }
  }

  const drawer = document.querySelector('cart-items-component');
  const sectionId = drawer instanceof HTMLElement ? drawer.dataset.sectionId : undefined;
  const body = sectionId ? { items: [item], sections: sectionId } : { items: [item] };

  const res = await fetch(routes.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    throw new Error(data.description || data.message || 'add failed');
  }

  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (typeof o.beforeNotify === 'function') await o.beforeNotify();

  o.host.dispatchEvent(
    new CartAddEvent(cart || {}, o.host.id, {
      source: 'mnr-' + o.groupType,
      itemCount: cart ? cart.item_count : 1,
      productId: o.productId ? String(o.productId) : undefined,
      variantId: String(o.variantId),
      sections: data.sections,
    })
  );
  /** @type {any} */ (document.querySelector('cart-drawer-component'))?.open?.();

  return { cart, item };
}

class MnrDesignPicker extends HTMLElement {
  /** @type {Design[]} ordered picks, repeats allowed */
  #picked = [];
  /** @type {number} target count set by the host (tier or set size) */
  #target = 1;
  /** @type {HTMLElement | null} element to return focus to after the drawer closes */
  #returnFocusTo = null;
  #bound = false;
  #activeTag = '';

  connectedCallback() {
    // The theme editor can re-connect the same element; keep state, bind once.
    if (!this.#bound) {
      this.#target = Math.max(1, parseInt(this.dataset.slots || '1', 10) || 1);
    }
    this.#warnMultiVariant();
    this.#render();

    if (!this.#bound) {
      this.#bound = true;
      this.addEventListener('click', this.#onClick);
      this.#search?.addEventListener('input', this.#applyFilters);
      this.#dialog?.addEventListener('close', this.#onNativeClose);
      this.#dialogHost?.addEventListener('dialog:close', this.#onNativeClose);
    }
  }

  /* ── Public API ─────────────────────────────────────────────── */

  /** @param {number} n */
  setSlotCount(n) {
    const count = Math.min(this.max, Math.max(1, Math.floor(Number(n)) || 1));
    if (count === this.#target && this.#picked.length <= count) return;
    this.#target = count;
    if (this.#picked.length > count) this.#picked = this.#picked.slice(0, count); // trim from the END, never the start
    this.dataset.slots = String(count);
    this.#render();
    this.#emit();
  }

  /** @returns {Array<{variantId: number, name: string}>} */
  getSelection() {
    return this.#picked.map((d) => ({ variantId: d.variantId, name: d.name }));
  }

  /**
   * Adds the tile for this variant id (used for ?preselect=).
   * @param {number | string} variantId
   * @returns {boolean} whether it was added
   */
  preselect(variantId) {
    const id = String(variantId);
    const tile = this.#tiles.find((t) => t.dataset.variantId === id && t.dataset.available === '1');
    if (!tile || this.#picked.length >= this.max) return false;
    this.#picked.push(this.#tileData(tile));
    this.#render();
    this.#emit();
    return true;
  }

  /** 'build' | 'browse' (attribute `mode`; anything but 'browse' is build). */
  get mode() {
    return this.getAttribute('mode') === 'browse' ? 'browse' : 'build';
  }

  /** @param {string} value */
  set mode(value) {
    const next = value === 'browse' ? 'browse' : 'build';
    if (next === this.mode) return;
    this.setAttribute('mode', next);
    this.#render();
    this.dispatchEvent(new CustomEvent('mnr:mode-change', { bubbles: true, detail: { mode: next } }));
  }

  /**
   * Opens the drawer from outside (swatch row, tier prompt).
   * @param {object} [o]
   * @param {'browse'|'build'} [o.mode] - switches the mode first
   * @param {number} [o.slots] - target count for build mode
   * @param {number|string|Array<number|string>} [o.preselect] - variant id(s) to add first
   * @param {HTMLElement} [o.opener] - element to return focus to on close
   */
  open(o = {}) {
    if (o.mode) this.mode = o.mode;
    if (o.slots) this.setSlotCount(o.slots);
    if (o.preselect != null) {
      const ids = Array.isArray(o.preselect) ? o.preselect : [o.preselect];
      for (const id of ids) {
        if (!this.#picked.some((d) => String(d.variantId) === String(id))) this.preselect(id);
      }
    }
    if (this.#dialog?.open) return;
    const fallback = this.#slotRow?.querySelector('[data-mnr="slot"]');
    const active = document.activeElement;
    this.#open(o.opener || (active instanceof HTMLElement && active !== document.body ? active : fallback) || this);
  }

  /** Closes the drawer from outside (selection kept); resolves after the closing animation. */
  close() {
    return this.#close();
  }

  get isOpen() {
    return Boolean(this.#dialog?.open);
  }

  get filled() {
    return this.#picked.length;
  }

  /** The host's target count (tier or set size), regardless of picks. */
  get target() {
    return this.#target;
  }

  /** Slots shown: the target, or the pick count when it exceeds the target. */
  get total() {
    return Math.max(this.#target, this.#picked.length);
  }

  /** Ceiling on the number of designs (data-max). */
  get max() {
    return Math.max(1, parseInt(this.dataset.max || '12', 10) || 12);
  }

  /* ── DOM lookups ────────────────────────────────────────────── */

  get #slotRow() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-mnr="slots"]'));
  }
  get #slotTemplate() {
    return /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-mnr="slot-template"]'));
  }
  get #dialogHost() {
    return /** @type {any} */ (this.querySelector('[data-mnr="dialog-host"]'));
  }
  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog[data-mnr="drawer"]'));
  }
  get #search() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mnr="search"]'));
  }
  get #tiles() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-mnr="tile"]')));
  }
  get #labels() {
    const d = this.dataset;
    return {
      slotEmpty: d.labelSlotEmpty || 'Design [n]',
      slotChoose: d.labelSlotChoose || 'Design [n]',
      slotReplace: d.labelSlotReplace || '[name] ([n])',
      slotRemove: d.labelSlotRemove || '[name]',
      selectedN: d.labelSelectedN || '[n]',
      remaining: d.labelRemaining || '[n]',
      complete: d.labelComplete || '',
      maxReached: d.labelMaxReached || '',
      unit: d.unit || 'design',
      units: d.units || 'designs',
      titleBuild: d.labelTitleBuild || '',
      titleBrowse: d.labelTitleBrowse || '',
      goTo: d.labelGoTo || '[name]',
      current: d.labelCurrent || '',
      soldOutText: d.labelSoldOut || '',
    };
  }

  /* ── Rendering ──────────────────────────────────────────────── */

  #render() {
    this.#renderSlots();
    this.#renderFooter();
    this.#renderTitle();
    this.#syncTiles();
  }

  /** Drawer title per mode; the build title is the server-rendered one. */
  #renderTitle() {
    const title = this.querySelector('[data-mnr="drawer-title"]');
    if (!title) return;
    const labels = this.#labels;
    const text = this.mode === 'browse' ? labels.titleBrowse : labels.titleBuild;
    if (text) title.textContent = text;
  }

  #renderSlots() {
    const row = this.#slotRow;
    const tpl = this.#slotTemplate;
    if (!row || !tpl) return;

    const labels = this.#labels;
    const frag = document.createDocumentFragment();

    for (let i = 0; i < this.total; i++) {
      const data = this.#picked[i] || null;
      const n = String(i + 1);
      const wrap = /** @type {HTMLElement} */ (tpl.content.firstElementChild?.cloneNode(true));
      const slot = /** @type {HTMLButtonElement} */ (wrap.querySelector('[data-mnr="slot"]'));
      const img = /** @type {HTMLElement} */ (wrap.querySelector('[data-mnr="slot-img"]'));
      const name = /** @type {HTMLElement} */ (wrap.querySelector('[data-mnr="slot-name"]'));
      const remove = /** @type {HTMLButtonElement} */ (wrap.querySelector('[data-mnr="slot-remove"]'));
      const removeLabel = /** @type {HTMLElement} */ (wrap.querySelector('[data-mnr="slot-remove-label"]'));

      slot.dataset.index = String(i);
      remove.dataset.index = String(i);

      if (data) {
        wrap.classList.add('mnr-dp__slot-wrap--filled');
        img.replaceChildren();
        if (data.img) {
          const el = document.createElement('img');
          el.setAttribute('src', data.img);
          el.setAttribute('alt', '');
          el.setAttribute('width', '120');
          el.setAttribute('height', '160');
          el.setAttribute('loading', 'lazy');
          el.setAttribute('decoding', 'async');
          img.appendChild(el);
        }
        name.textContent = data.name;
        slot.setAttribute('aria-label', fill(labels.slotReplace, { n, name: data.name }));
        remove.hidden = false;
        removeLabel.textContent = fill(labels.slotRemove, { name: data.name });
      } else {
        const num = img.querySelector('.mnr-dp__slot-num');
        if (num) num.textContent = n;
        name.textContent = fill(labels.slotEmpty, { n });
        slot.setAttribute('aria-label', fill(labels.slotChoose, { n }));
        remove.hidden = true;
      }

      frag.appendChild(wrap);
    }

    row.replaceChildren(frag);
  }

  /** Footer: "N designs gekozen · Nog 2 te kiezen / Compleet" + mini strip. */
  #renderFooter() {
    const labels = this.#labels;
    const filled = this.filled;
    const remaining = Math.max(0, this.#target - filled);
    const atMax = filled >= this.max;

    const text = this.querySelector('[data-mnr="progress-text"]');
    if (text) {
      const parts = [fill(labels.selectedN, { n: String(filled), unit: filled === 1 ? labels.unit : labels.units })];
      if (atMax && labels.maxReached) parts.push(labels.maxReached);
      else if (remaining > 0) parts.push(fill(labels.remaining, { n: String(remaining) }));
      else if (labels.complete) parts.push(labels.complete);
      text.textContent = parts.join(' · ');
    }

    const mini = this.querySelector('[data-mnr="mini"]');
    if (!mini) return;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < this.total; i++) {
      const data = this.#picked[i];
      const cell = document.createElement('span');
      cell.className = 'mnr-dp__mini-slot';
      if (data) {
        cell.classList.add('mnr-dp__mini-slot--filled');
        if (data.img) {
          const img = document.createElement('img');
          img.setAttribute('src', data.img);
          img.setAttribute('alt', '');
          img.setAttribute('width', '30');
          img.setAttribute('height', '40');
          img.setAttribute('loading', 'lazy');
          cell.appendChild(img);
        }
      } else {
        cell.textContent = String(i + 1);
      }
      frag.appendChild(cell);
    }
    mini.replaceChildren(frag);
  }

  /** Tile counters, picked ring, plus/minus states. */
  #syncTiles() {
    const counts = new Map();
    for (const d of this.#picked) counts.set(d.variantId, (counts.get(d.variantId) || 0) + 1);
    const atMax = this.filled >= this.max;
    const browse = this.mode === 'browse';
    const labels = this.#labels;
    for (const tile of this.#tiles) {
      const n = counts.get(parseInt(tile.dataset.variantId || '0', 10)) || 0;
      const soldOut = tile.dataset.available !== '1';
      if (n > 0) tile.dataset.picked = String(n);
      else delete tile.dataset.picked;
      const count = tile.querySelector('[data-mnr="tile-count"]');
      if (count) count.textContent = String(n);
      const main = tile.querySelector('[data-mnr="tile-add"]');
      if (main) {
        // Keep the server-rendered build label and restore it after browse.
        if (!main.dataset.labelBuild) main.dataset.labelBuild = main.getAttribute('aria-label') || '';
        if (browse) {
          const parts = [fill(labels.goTo, { name: tile.dataset.name || '' })];
          if (tile.dataset.current === '1' && labels.current) parts.push(labels.current);
          if (soldOut && labels.soldOutText) parts.push(labels.soldOutText);
          main.setAttribute('aria-label', parts.join(', '));
          main.removeAttribute('aria-pressed');
          main.removeAttribute('aria-disabled');
          // Browse: sold-out designs stay reachable (their page handles notify).
          if (main instanceof HTMLButtonElement) main.disabled = false;
          continue;
        }
        main.setAttribute('aria-label', main.dataset.labelBuild);
        main.setAttribute('aria-pressed', String(n > 0));
        if (soldOut) main.setAttribute('aria-disabled', 'true');
        else main.removeAttribute('aria-disabled');
        if (main instanceof HTMLButtonElement) main.disabled = soldOut;
      }
      const plus = tile.querySelector('[data-mnr="tile-plus"]');
      if (plus instanceof HTMLButtonElement) plus.disabled = soldOut;
      const minus = tile.querySelector('[data-mnr="tile-minus"]');
      if (minus instanceof HTMLButtonElement) minus.disabled = n === 0;
      // At the ceiling the controls stay enabled: a click reports the limit
      // so the host can explain it (set full → mix & match hint, etc.).
      tile.classList.toggle('mnr-dp__tile--at-max', atMax && n === 0);
    }
  }

  /* ── Events ─────────────────────────────────────────────────── */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof Element)) return;

    const slot = target.closest('[data-mnr="slot"]');
    if (slot instanceof HTMLElement) return this.#open(slot);

    const remove = target.closest('[data-mnr="slot-remove"]');
    if (remove instanceof HTMLElement) return this.#removeAt(parseInt(remove.dataset.index || '0', 10));

    if (target.closest('[data-mnr="to-build"]')) {
      this.mode = 'build';
      const focus = this.querySelector('[data-mnr="tile-add"]:not(:disabled)');
      if (focus instanceof HTMLElement) focus.focus();
      return;
    }

    if (this.mode === 'browse') {
      // Browse: a tile is a link to that design's page; nothing is picked.
      const tile = target.closest('[data-mnr="tile"]');
      if (tile instanceof HTMLElement && tile.dataset.url) {
        location.href = tile.dataset.url;
        return;
      }
    }

    const plus = target.closest('[data-mnr="tile-add"], [data-mnr="tile-plus"]');
    if (plus instanceof HTMLButtonElement && !plus.disabled) return this.#add(plus);

    const minus = target.closest('[data-mnr="tile-minus"]');
    if (minus instanceof HTMLButtonElement && !minus.disabled) return this.#removeLast(minus);

    if (target.closest('[data-mnr="apply"]')) return this.#done();

    const chip = target.closest('[data-mnr="chip"]');
    if (chip instanceof HTMLElement) return this.#toggleChip(chip);

    if (target.closest('[data-mnr="close"]')) return this.#close();
  };

  /** @param {HTMLElement} opener */
  #open(opener) {
    this.#returnFocusTo = opener;
    const host = this.#dialogHost;
    const dialog = this.#dialog;
    if (!dialog) return;
    if (host && typeof host.showDialog === 'function' && typeof dialog.showModal === 'function') {
      host.showDialog();
    } else if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.classList.add('mnr-dp__drawer--fallback');
      dialog.setAttribute('open', '');
    }
  }

  /** Plus: fills the next slot at once (grows the row up to the ceiling). */
  #add(control) {
    const tile = control.closest('[data-mnr="tile"]');
    if (!(tile instanceof HTMLElement) || tile.dataset.available !== '1') return;
    if (this.#picked.length >= this.max) {
      this.dispatchEvent(new CustomEvent('mnr:selection-limit', { bubbles: true, detail: { max: this.max } }));
      return;
    }
    this.#picked.push(this.#tileData(tile));
    this.#render();
    this.#emit();
  }

  /** Minus: removes the LAST occurrence of this design; later picks shift left. */
  #removeLast(control) {
    const tile = control.closest('[data-mnr="tile"]');
    if (!(tile instanceof HTMLElement)) return;
    const id = parseInt(tile.dataset.variantId || '0', 10);
    for (let i = this.#picked.length - 1; i >= 0; i--) {
      if (this.#picked[i].variantId === id) {
        this.#picked.splice(i, 1);
        break;
      }
    }
    this.#render();
    this.#emit();
  }

  /** × on a slot: removes that pick; later picks shift left. */
  #removeAt(index) {
    if (index < 0 || index >= this.#picked.length) return;
    this.#picked.splice(index, 1);
    this.#render();
    this.#emit();
    const slot = this.#slotRow?.querySelector(`[data-mnr="slot"][data-index="${index}"]`);
    if (slot instanceof HTMLElement) slot.focus();
  }

  /** "Selectie toepassen": nothing to commit any more — close and move focus to the next step. */
  async #done() {
    await this.#close();
    const empty = this.#slotRow?.querySelector('[data-mnr="slot-wrap"]:not(.mnr-dp__slot-wrap--filled) [data-mnr="slot"]');
    if (empty instanceof HTMLElement) {
      empty.focus();
      return;
    }
    this.dispatchEvent(
      new CustomEvent('mnr:picker-closed', { bubbles: true, detail: { filled: this.filled, total: this.total } })
    );
  }

  async #close() {
    const host = this.#dialogHost;
    const dialog = this.#dialog;
    if (!dialog) return;
    if (host && typeof host.closeDialog === 'function' && typeof dialog.showModal === 'function') {
      await host.closeDialog();
    } else if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.classList.remove('mnr-dp__drawer--fallback');
    }
    this.#afterClose();
  }

  /** Native close (Escape / backdrop via Horizon). Selection stays as it is. */
  #onNativeClose = () => {
    if (this.#dialog?.open) return;
    this.#afterClose();
  };

  #afterClose() {
    if (!this.#returnFocusTo) return;
    const target = this.#returnFocusTo.isConnected
      ? this.#returnFocusTo
      : this.#slotRow?.querySelector('[data-mnr="slot"]');
    this.#returnFocusTo = null;
    if (target instanceof HTMLElement) target.focus();
  }

  /** @param {HTMLElement} chip */
  #toggleChip(chip) {
    const tag = chip.dataset.tag || '';
    this.#activeTag = this.#activeTag === tag ? '' : tag;
    this.querySelectorAll('[data-mnr="chip"]').forEach((c) => {
      c.setAttribute('aria-pressed', String(c instanceof HTMLElement && c.dataset.tag === this.#activeTag));
    });
    this.#applyFilters();
  }

  #applyFilters = () => {
    const q = (this.#search?.value || '').trim().toLowerCase();
    const tag = this.#activeTag;
    let visible = 0;
    for (const tile of this.#tiles) {
      const name = (tile.dataset.name || '').toLowerCase();
      const tags = (tile.dataset.tags || '').split(',');
      const show = (!q || name.includes(q)) && (!tag || tags.includes(tag));
      tile.hidden = !show;
      if (show) visible++;
    }
    const empty = this.querySelector('[data-mnr="empty"]');
    if (empty instanceof HTMLElement) empty.hidden = visible > 0;
  };

  /* ── Helpers ────────────────────────────────────────────────── */

  /** @param {HTMLElement} tile @returns {Design} */
  #tileData(tile) {
    return {
      variantId: parseInt(tile.dataset.variantId || '0', 10),
      name: tile.dataset.name || '',
      img: tile.dataset.img || '',
      price: parseInt(tile.dataset.price || '0', 10),
    };
  }

  #emit() {
    this.dispatchEvent(
      new CustomEvent('mnr:selection-change', {
        bubbles: true,
        detail: { selection: this.getSelection(), filled: this.filled, total: this.total },
      })
    );
  }

  #warnMultiVariant() {
    for (const tile of this.#tiles) {
      if (tile.dataset.variantWarn) {
        console.warn(
          `[mnr-design-picker] "${tile.dataset.name}" has ${tile.dataset.variantWarn} variants; a design should have one. Using the first variant.`
        );
      }
    }
  }
}

/**
 * Turns "&amp;" and friends back into characters (labels are set via textContent).
 * @param {string} str
 */
function decodeEntities(str) {
  if (!str.includes('&')) return str;
  return new DOMParser().parseFromString(str, 'text/html').documentElement.textContent || str;
}

/**
 * Replaces [key] placeholders in a label.
 * @param {string} template
 * @param {Record<string, string>} values
 */
function fill(template, values) {
  return template.replace(/\[(\w+)\]/g, (m, key) => (key in values ? values[key] : m));
}

if (!customElements.get('mnr-design-picker')) {
  customElements.define('mnr-design-picker', MnrDesignPicker);
}


/* ══════════════════════════════════════════════════════════════════════════
   Shared host behaviour for both bundle blocks (Route B, 19 Sep 2026).
   blocks/mnr-starter-set.liquid and blocks/mnr-mix-match.liquid render the
   markup (cards with data-variant-id / data-design-count / data-price /
   data-loose-value / data-available / data-title / data-variant, hooks as
   data-mnr-bundle="…") and define a one-line subclass of this element.
   One code path: card choice, upgrade up (never down) with a persistent
   message, ceiling message, Horizon variant sync, value line, add button
   via addBundleToCart, dataLayer.

   Optional config flags (PDP swatches host, 11 Oct 2026; all default to the
   old behaviour when absent):
     syncVariant: false     never fetch/dispatch Horizon variant updates — the
                            host sits on a loose design page, not on the
                            bundle product's page
     inlineAdd: true        below the needed count the add button stays
                            visible but disabled with the "Kies nog N" label
                            instead of swapping to the "goto" link
     closePickerOnAdd: true close the picker dialog after a successful add,
                            before the cart event opens the cart drawer
     preselectId            variant id added when the picker switches to build
                            mode with nothing picked yet (the page's design)
   ══════════════════════════════════════════════════════════════════════════ */

export class MnrBundleHost extends HTMLElement {
  #cfg = null;
  #busy = false;
  #bound = false;
  /** @type {AbortController | null} */
  #fetchAbort = null;

  async connectedCallback() {
    try {
      this.#cfg = JSON.parse(this.dataset.config || '{}');
    } catch (e) {
      console.error('[mnr-bundle] bad config', e);
      return;
    }
    if (!this.#cfg.productId) return;
    // Liquid's `t` filter HTML-escapes its output when it interpolates
    // parameters ("Mix &amp; Match"); the labels are inserted as text, so
    // decode them once here.
    for (const [k, v] of Object.entries(this.#cfg.labels || {})) {
      if (typeof v === 'string') this.#cfg.labels[k] = decodeEntities(v);
    }

    await customElements.whenDefined('mnr-design-picker');
    const picker = this.picker;
    if (!picker) return;

    if (!this.#bound) {
      this.#bound = true;
      this.addEventListener('click', this.#onClick);
      this.addEventListener('mnr:selection-change', () => {
        this.#followSelection();
        this.#update();
      });
      this.addEventListener('mnr:selection-limit', () => this.#showLimit());
      this.addEventListener('mnr:picker-closed', () => {
        const add = this.$('add');
        if (add instanceof HTMLElement && !add.hidden) add.focus();
      });
      this.addEventListener('mnr:mode-change', (event) => {
        const mode = /** @type {CustomEvent} */ (event).detail?.mode;
        const p = this.picker;
        if (mode === 'build' && p && p.filled === 0 && this.#cfg.preselectId) p.preselect(this.#cfg.preselectId);
      });
    }

    this.#warnPriceMismatch();
    this.#applyPreselect(picker);
    this.#update();

    // The page rendered with Shopify's default variant while the block
    // preselects another card: sync Horizon once (no fetch when they match).
    const card = this.current;
    if (card && String(this.#cfg.pageVariantId) !== String(card.dataset.variantId)) {
      this.#syncVariant(card);
    }
  }

  get cfg() {
    return this.#cfg;
  }

  get picker() {
    return /** @type {MnrDesignPicker | null} */ (this.querySelector('mnr-design-picker'));
  }

  /** @param {string} sel */
  $(sel) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-mnr-bundle="${sel}"]`));
  }

  get cards() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-mnr-bundle="card"]')));
  }

  get current() {
    return this.cards.find((c) => c.getAttribute('aria-checked') === 'true') || this.cards[0] || null;
  }

  money(cents) {
    try {
      return formatMoney(cents, this.#cfg.moneyFormat, this.#cfg.currency);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /* ── Rendering ─────────────────────────────────────────────── */

  #update() {
    const picker = this.picker;
    const card = this.current;
    if (!picker || !card) return;
    const cfg = this.#cfg;
    const L = cfg.labels;
    const filled = picker.filled;
    const slots = picker.total;
    const need = Number(card.dataset.designCount);
    const price = Number(card.dataset.price);
    const loose = Number(card.dataset.looseValue);

    const count = this.$('count');
    if (count) count.textContent = fill(L.selectedCount, { filled: String(filled), total: String(slots) });

    // Value line: struck loose value, bundle price, saving (with % when the label asks for it).
    const totalEl = this.$('total');
    const looseEl = this.$('loose');
    const savingsEl = this.$('savings');
    if (totalEl) totalEl.textContent = this.money(price);
    if (looseEl) {
      looseEl.hidden = !(loose > price);
      if (loose > price) looseEl.textContent = this.money(loose);
    }
    if (savingsEl) {
      const saved = loose - price;
      savingsEl.hidden = !(saved > 0 && loose > 0);
      if (saved > 0 && loose > 0) {
        const pct = Math.round((saved / loose) * 1000) / 10;
        savingsEl.textContent = fill(L.savings, { amount: this.money(saved), pct: String(pct).replace('.', ',') });
      }
    }

    // Optional hint (PDP drawer): what the next pick(s) bring. Below the card's
    // count: finish this card; at the count: the next larger card's per-unit
    // price; on the largest card: lowest price reached.
    const hintEl = this.$('hint');
    if (hintEl && L.hintMore && L.hintLowest) {
      let text = '';
      if (filled < need) {
        text = fill(L.hintMore, { n: String(need - filled), price: this.money(Math.round(price / need)), unit: cfg.unit });
      } else {
        const next = this.cards
          .filter((c) => Number(c.dataset.designCount) > need && c.dataset.available === '1')
          .sort((a, b) => Number(a.dataset.designCount) - Number(b.dataset.designCount))[0];
        if (next) {
          const nextCount = Number(next.dataset.designCount);
          text = fill(L.hintMore, {
            n: String(nextCount - filled),
            price: this.money(Math.round(Number(next.dataset.price) / nextCount)),
            unit: cfg.unit,
          });
        } else {
          text = fill(L.hintLowest, { unit: cfg.unit });
        }
      }
      hintEl.textContent = text;
      hintEl.hidden = !text;
    }

    // Control: link to step 2 while designs are missing; add button when every slot is filled.
    // With inlineAdd (PDP drawer) there is no step-2 link: the add button stays
    // visible and disabled, carrying the "Kies nog N" label.
    const add = this.$('add');
    const label = this.$('add-label');
    const goto = this.$('goto');
    const gotoLabel = this.$('goto-label');
    if (!add || !label) return;
    if (!cfg.inlineAdd && (!goto || !gotoLabel)) return;
    const available = card.dataset.available === '1';
    const disable = (text) => {
      if (goto) goto.hidden = true;
      add.hidden = false;
      label.textContent = text;
      /** @type {HTMLButtonElement} */ (add).disabled = true;
      add.setAttribute('aria-disabled', 'true');
    };
    if (this.#busy) return disable(L.busy);
    if (!available) return disable(L.soldOut);
    if (filled < need) {
      const missing = need - filled;
      const text = fill(L.chooseMore, { n: String(missing), unit: missing === 1 ? cfg.unit : cfg.units });
      if (cfg.inlineAdd || !goto || !gotoLabel) return disable(text);
      gotoLabel.textContent = text;
      goto.hidden = false;
      add.hidden = true;
    } else {
      label.textContent = `${cfg.buttonLabel} · ${this.money(price)}`;
      /** @type {HTMLButtonElement} */ (add).disabled = false;
      add.removeAttribute('aria-disabled');
      add.hidden = false;
      goto.hidden = true;
    }
  }

  /**
   * Upgrade (19 Sep 2026): more designs than the card holds → the smallest
   * available card that fits, as if clicked, plus a persistent message.
   * Never downwards: fewer designs keep the card with empty slots.
   */
  #followSelection() {
    const picker = this.picker;
    const card = this.current;
    if (!picker || !card) return;
    const filled = picker.filled;
    if (filled <= Number(card.dataset.designCount)) return;
    const next = this.cards
      .filter((c) => Number(c.dataset.designCount) >= filled && c.dataset.available === '1')
      .sort((a, b) => Number(a.dataset.designCount) - Number(b.dataset.designCount))[0];
    if (!next || next === card) return;
    this.#selectCard(next);
    const L = this.#cfg.labels;
    const missing = Number(next.dataset.designCount) - filled;
    const values = {
      set: next.dataset.title || '',
      price: this.money(Number(next.dataset.price)),
      n: String(missing),
      unit: missing === 1 ? this.#cfg.unit : this.#cfg.units,
    };
    this.#showMessage(fill(missing > 0 ? L.upgradedMore : L.upgraded, values));
  }

  /** The largest card is full: refuse with the configured message (+ optional link). */
  #showLimit() {
    const L = this.#cfg.labels;
    const text = fill(L.limit, { n: String(this.#cfg.max) });
    const link = this.#cfg.limitUrl && L.limitLink ? { href: this.#cfg.limitUrl, label: L.limitLink } : null;
    this.#showMessage(text, link);
  }

  /**
   * @param {string} text
   * @param {Object | null} [link] - href and label
   */
  #showMessage(text, link = null) {
    const el = this.$('message');
    if (!el) return;
    el.replaceChildren();
    el.append(document.createTextNode(text));
    if (link) {
      el.append(document.createTextNode(' '));
      const a = document.createElement('a');
      a.setAttribute('href', link.href);
      a.textContent = link.label;
      el.append(a);
    }
    el.hidden = false;
  }

  #clearMessage() {
    const el = this.$('message');
    if (el) {
      el.hidden = true;
      el.replaceChildren();
    }
  }

  /* ── Card choice + Horizon sync ───────────────────────────── */

  /** @param {HTMLElement} card */
  #selectCard(card) {
    this.cards.forEach((c) => c.setAttribute('aria-checked', String(c === card)));
    this.picker?.setSlotCount(Number(card.dataset.designCount));
    this.#update();
    this.#syncVariant(card);
  }

  /**
   * The same request Horizon's own variant picker makes, then the same
   * events dispatched from inside the section, so the media gallery, price
   * block and any buy-buttons form follow. URL updated with replaceState.
   * @param {HTMLElement} card
   */
  #syncVariant(card) {
    const cfg = this.#cfg;
    // A host on a loose design page must not re-render that page with the
    // bundle product's variant (PDP swatches).
    if (cfg.syncVariant === false) return;
    const variantId = card.dataset.variantId || '';
    let variant = null;
    try {
      variant = JSON.parse(card.dataset.variant || 'null');
    } catch (e) {
      variant = null;
    }
    this.dispatchEvent(new VariantSelectedEvent({ id: variantId }));

    const url = new URL(cfg.productUrl, location.origin);
    url.searchParams.set('variant', variantId);
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    fetch(url.toString(), { signal: this.#fetchAbort.signal })
      .then((r) => r.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
        this.dispatchEvent(new VariantUpdateEvent(variant, this.id, { html, productId: String(cfg.productId) }));
        const here = new URL(location.href);
        here.searchParams.set('variant', variantId);
        history.replaceState(history.state, '', here.toString());
      })
      .catch((e) => {
        if (e?.name !== 'AbortError') console.warn('[mnr-bundle] variant fetch failed', e);
      });
  }

  /* ── Events ────────────────────────────────────────────────── */

  #onClick = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const card = t.closest('[data-mnr-bundle="card"]');
    if (card instanceof HTMLElement) {
      if (card.dataset.available !== '1' || card.getAttribute('aria-checked') === 'true') return;
      this.#clearMessage();
      return this.#selectCard(card);
    }
    if (t.closest('[data-mnr-bundle="add"]')) return this.#add();
    if (t.closest('[data-mnr-bundle="goto"]')) {
      event.preventDefault();
      this.$('step2')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const empty = this.picker?.querySelector('[data-mnr="slot-wrap"]:not(.mnr-dp__slot-wrap--filled) [data-mnr="slot"]');
      if (empty instanceof HTMLElement) empty.click();
      return;
    }
  };

  /** ?preselect=id,id fills the picker; the count then selects the card that fits. */
  #applyPreselect(picker) {
    const ids = (new URLSearchParams(location.search).get('preselect') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) return;
    ids.forEach((id) => picker.preselect(id));
  }

  /** Data check: a staffels price that differs from the variant price is ignored, but said. */
  #warnPriceMismatch() {
    for (const card of this.cards) {
      if (card.dataset.priceWarn) {
        console.warn(
          `[mnr-bundle] staffels price ${card.dataset.priceWarn} for ${card.dataset.designCount} designs differs from the variant price ${card.dataset.price}; the variant price is used.`
        );
      }
    }
  }

  /* ── Add to cart: one line via the shared function ─────────── */

  async #add() {
    if (this.#busy) return;
    const picker = this.picker;
    const card = this.current;
    const cfg = this.#cfg;
    if (!picker || !card) return;
    const need = Number(card.dataset.designCount);
    if (picker.filled < need || card.dataset.available !== '1') return;

    const error = this.$('error');
    this.#busy = true;
    if (error) error.hidden = true;
    this.#update();

    const selection = picker.getSelection().slice(0, need);
    const replaceKey = new URLSearchParams(location.search).get('replace') || undefined;

    try {
      await addBundleToCart({
        variantId: card.dataset.variantId || '',
        selection,
        groupType: cfg.groupType,
        designCount: need,
        replaceKey,
        host: this,
        productId: String(cfg.productId),
        // PDP: the picker dialog closes before the cart drawer opens.
        beforeNotify: cfg.closePickerOnAdd && picker.isOpen ? () => picker.close() : undefined,
      });

      if (replaceKey) {
        const here = new URL(location.href);
        here.searchParams.delete('replace');
        history.replaceState(history.state, '', here.toString());
      }

      const live = this.$('live');
      if (live) live.textContent = cfg.labels.added;

      const price = Number(card.dataset.price);
      this.#track({
        event: 'add_to_cart',
        block_id: this.id,
        bundle_mode: cfg.bundleMode,
        bundle_size: need,
        ecommerce: {
          currency: cfg.currency,
          value: price / 100,
          items: [
            {
              item_id: String(card.dataset.variantId),
              item_name: `${cfg.productTitle} – ${card.dataset.title || ''}`,
              quantity: 1,
              price: price / 100,
            },
          ],
        },
      });
    } catch (e) {
      if (error) {
        error.textContent = e.message || cfg.labels.errorGeneric;
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      this.#update();
    }
  }

  #track(payload) {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }
}

export { MnrDesignPicker, fill };
